import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { FileText, Save } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Spinner } from '@/components/ui/spinner';
import api from '@/lib/axios';
import { API_ENDPOINTS } from '@/constants/api-endpoints';
import toast from 'react-hot-toast';

interface SettingRow { key: string; value: string }

const settingsApi = {
  getAll: async (): Promise<SettingRow[]> => {
    const res = await api.get(API_ENDPOINTS.SETTINGS.LIST);
    const raw = (res.data as any)?.data ?? res.data;
    return Array.isArray(raw) ? raw : [];
  },
  update: async (key: string, value: string) => {
    await api.put(API_ENDPOINTS.SETTINGS.UPDATE(key), { value });
  },
};

const FIELDS = [
  { key: 'zipto_gstin', label: 'Bookfleet GSTIN', placeholder: '22AAAAA0000A1Z5', hint: 'Printed on B2B tax invoices' },
  { key: 'zipto_legal_name', label: 'Legal Name', placeholder: 'Zipto Hyperlogistics Pvt. Ltd.' },
  { key: 'zipto_gst_state', label: 'Place of Supply (State)', placeholder: 'Odisha' },
  { key: 'zipto_invoice_address', label: 'Registered Address', placeholder: 'Street, City, State, PIN' },
] as const;

/**
 * Admin control for Zipto's GST identity used on B2B tax invoices.
 * When a customer enters their GSTIN at checkout, the invoice prints both
 * Zipto's GSTIN (set here) and the customer's, with a CGST + SGST split.
 */
export function GstSettingsCard() {
  const queryClient = useQueryClient();
  const { data: settings = [], isLoading } = useQuery({
    queryKey: ['system-settings'],
    queryFn: settingsApi.getAll,
  });

  const [values, setValues] = useState<Record<string, string>>({});

  useEffect(() => {
    const map: Record<string, string> = {};
    settings.forEach((s) => { map[s.key] = s.value; });
    setValues({
      zipto_gstin: map.zipto_gstin ?? '',
      zipto_legal_name: map.zipto_legal_name ?? '',
      zipto_gst_state: map.zipto_gst_state ?? '',
      zipto_invoice_address: map.zipto_invoice_address ?? '',
    });
  }, [settings]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      for (const f of FIELDS) {
        await settingsApi.update(f.key, (values[f.key] ?? '').trim());
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['system-settings'] });
      toast.success('GST invoice settings updated');
    },
    onError: () => toast.error('Failed to update GST settings'),
  });

  return (
    <Card>
      <CardContent className="p-5">
        <div className="mb-4 flex items-center gap-2">
          <FileText className="h-5 w-5 text-primary" />
          <div>
            <h3 className="text-base font-semibold">GST & Tax Invoice (B2B)</h3>
            <p className="text-sm text-muted-foreground">
              Your company GST details, printed on tax invoices for business customers who enter their GSTIN.
            </p>
          </div>
        </div>

        {isLoading ? (
          <div className="flex justify-center py-6"><Spinner /></div>
        ) : (
          <>
            <div className="grid gap-4 sm:grid-cols-2">
              {FIELDS.map((f) => (
                <div key={f.key} className={f.key === 'zipto_invoice_address' ? 'sm:col-span-2' : ''}>
                  <Label htmlFor={f.key}>{f.label}</Label>
                  <Input
                    id={f.key}
                    className="mt-1"
                    placeholder={f.placeholder}
                    value={values[f.key] ?? ''}
                    onChange={(e) => setValues((v) => ({ ...v, [f.key]: e.target.value }))}
                  />
                  {'hint' in f && f.hint ? (
                    <p className="text-xs text-muted-foreground mt-1">{f.hint}</p>
                  ) : null}
                </div>
              ))}
            </div>
            <div className="mt-4 flex justify-end">
              <Button onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending}>
                <Save className="h-4 w-4 mr-2" />
                {saveMutation.isPending ? 'Saving…' : 'Save GST Settings'}
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
